/**
 * Location formatting utility
 * Ensures consistent capitalization and language-specific city names
 * First letter uppercase, rest lowercase - with proper Greek accents
 */

// City name mappings: English -> Greek (with proper accents/tonos)
const cityTranslations = {
  // Major Greek cities
  'athens': 'Αθήνα',
  'thessaloniki': 'Θεσσαλονίκη',
  'patras': 'Πάτρα',
  'heraklion': 'Ηράκλειο',
  'larissa': 'Λάρισα',
  'volos': 'Βόλος',
  'ioannina': 'Ιωάννινα',
  'chania': 'Χανιά',
  'rhodes': 'Ρόδος',
  'kavala': 'Καβάλα',
  'kalamata': 'Καλαμάτα',
  'piraeus': 'Πειραιάς',
  'peristeri': 'Περιστέρι',
  'nikaia': 'Νίκαια',
  'glyfada': 'Γλυφάδα',
  'kifisia': 'Κηφισιά',
  'marousi': 'Μαρούσι',
  'chalandri': 'Χαλάνδρι',
  'ilioupoli': 'Ηλιούπολη',
  'nea smyrni': 'Νέα Σμύρνη',
  'kallithea': 'Καλλιθέα',
  'agia paraskevi': 'Αγία Παρασκευή',
  'palaio faliro': 'Παλαιό Φάληρο',
  'keratsini': 'Κερατσίνι',
  'korydallos': 'Κορυδαλλός',
  'neo irakleio': 'Νέο Ηράκλειο',
  'zografou': 'Ζωγράφου',
  'vyronas': 'Βύρωνας',
  'galatsi': 'Γαλάτσι',
  'argyroupoli': 'Αργυρούπολη',
  'vrilissia': 'Βριλήσσια',
  'cholargos': 'Χολαργός',
  'papagou': 'Παπάγου',
  'psychiko': 'Ψυχικό',
  'filothei': 'Φιλοθέη',
  'ekali': 'Εκάλη',
  'penteli': 'Πεντέλη',
  'rafina': 'Ραφήνα',
  'marathon': 'Μαραθώνας',
  'spata': 'Σπάτα',
  'koropi': 'Κορωπί',
  'kalyvia': 'Καλύβια',
  'saronida': 'Σαρωνίδα',
  'anavyssos': 'Ανάβυσσος',
  'lavrio': 'Λαύριο',
  'keratea': 'Κερατέα',
  'markopoulo': 'Μαρκόπουλο',
  'chalkida': 'Χαλκίδα',
  'agrinio': 'Αγρίνιο',
  'lamia': 'Λαμία',
  'trikala': 'Τρίκαλα',
  'serres': 'Σέρρες',
  'alexandroupoli': 'Αλεξανδρούπολη',
  'xanthi': 'Ξάνθη',
  'komotini': 'Κομοτηνή',
  'drama': 'Δράμα',
  'veria': 'Βέροια',
  'katerini': 'Κατερίνη',
  'kozani': 'Κοζάνη',
  'florina': 'Φλώρινα',
  'kastoria': 'Καστοριά',
  'grevena': 'Γρεβενά',
  'corfu': 'Κέρκυρα',
  'zakynthos': 'Ζάκυνθος',
  'lefkada': 'Λευκάδα',
  'kefalonia': 'Κεφαλονιά',
  'lesvos': 'Λέσβος',
  'chios': 'Χίος',
  'samos': 'Σάμος',
  'kos': 'Κως',
  'mykonos': 'Μύκονος',
  'santorini': 'Σαντορίνη',
  'paros': 'Πάρος',
  'naxos': 'Νάξος',
  'syros': 'Σύρος',
  'crete': 'Κρήτη',
  'rethymno': 'Ρέθυμνο',
  'agios nikolaos': 'Άγιος Νικόλαος',
  'sitia': 'Σητεία',
  'ierapetra': 'Ιεράπετρα',
  'tripoli': 'Τρίπολη',
  'sparta': 'Σπάρτη',
  'argos': 'Άργος',
  'nafplio': 'Ναύπλιο',
  'corinth': 'Κόρινθος',
  'megara': 'Μέγαρα',
  'elefsina': 'Ελευσίνα',
  'salamina': 'Σαλαμίνα',
  'aegina': 'Αίγινα',
  // Countries
  'greece': 'Ελλάδα',
  'cyprus': 'Κύπρος',
};

// Reverse mapping: Greek -> English (normalized lowercase keys)
const cityTranslationsReverse = {};
Object.entries(cityTranslations).forEach(([en, el]) => {
  // Normalize Greek key (lowercase, handle accents)
  const normalizedEl = el.toLowerCase();
  cityTranslationsReverse[normalizedEl] = en.charAt(0).toUpperCase() + en.slice(1);
});

/**
 * Normalize a string for comparison (lowercase, trim)
 */
const normalizeForComparison = (str) => {
  if (!str) return '';
  return str.toLowerCase().trim();
};

/**
 * Format a location name with proper capitalization
 * Handles Greek text with proper accent preservation
 * @param {string} location - The location name to format
 * @returns {string} Formatted location name
 */
export const formatLocation = (location) => {
  if (!location || typeof location !== 'string') return '';

  // Trim and handle empty strings
  const trimmed = location.trim();
  if (!trimmed) return '';

  // Handle multi-word locations with proper capitalization
  // This preserves Greek accents while fixing case
  return trimmed
    .split(' ')
    .map(word => {
      if (!word) return '';
      // Convert to lowercase first, then capitalize first letter
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

/**
 * Get city name in the specified language
 * @param {string} city - City name (in any language)
 * @param {string} language - Target language ('en' or 'el')
 * @returns {string} City name in target language with proper formatting
 */
export const getCityByLanguage = (city, language = 'en') => {
  if (!city || typeof city !== 'string') return '';

  const normalized = normalizeForComparison(city);

  if (language === 'el') {
    // Try to find Greek translation
    if (cityTranslations[normalized]) {
      return cityTranslations[normalized];
    }
    // Check if it's already a Greek city name (return formatted)
    if (cityTranslationsReverse[normalized]) {
      // It's Greek, return the original with proper formatting
      return formatLocation(city);
    }
    // Unknown city, return formatted as-is
    return formatLocation(city);
  } else {
    // English requested
    // Check if it's a Greek name that needs translation
    if (cityTranslationsReverse[normalized]) {
      return cityTranslationsReverse[normalized];
    }
    // Check if it's already English
    if (cityTranslations[normalized]) {
      return formatLocation(city);
    }
    // Unknown city, return formatted as-is
    return formatLocation(city);
  }
};

/**
 * Format city and country together in the specified language
 * @param {string} city - City name
 * @param {string} country - Country name
 * @param {string} language - Target language ('en' or 'el')
 * @returns {string} Formatted "City, Country" string
 */
export const formatCityCountry = (city, country, language = 'en') => {
  const formattedCity = getCityByLanguage(city, language);
  const formattedCountry = getCityByLanguage(country, language);

  if (formattedCity && formattedCountry) {
    return `${formattedCity}, ${formattedCountry}`;
  }
  return formattedCity || formattedCountry || '';
};

/**
 * Format full address with city, postal code, and country
 * @param {string} city - City name
 * @param {string} postalCode - Postal code
 * @param {string} country - Country name
 * @param {string} language - Target language ('en' or 'el')
 * @returns {string} Formatted address string
 */
export const formatFullAddress = (city, postalCode, country, language = 'en') => {
  const formattedCity = getCityByLanguage(city, language);
  const formattedCountry = getCityByLanguage(country, language);

  let parts = [];
  if (formattedCity) parts.push(formattedCity);
  if (postalCode) parts.push(postalCode);
  if (formattedCountry) parts.push(formattedCountry);

  return parts.join(', ');
};

/**
 * Transform a list of cities to the target language
 * Also removes duplicates (same city in different languages)
 * @param {string[]} cities - Array of city names
 * @param {string} language - Target language ('en' or 'el')
 * @returns {string[]} Array of unique city names in target language
 */
export const transformCitiesByLanguage = (cities, language = 'en') => {
  if (!Array.isArray(cities)) return [];

  const uniqueCities = new Map();

  cities.forEach(city => {
    const translated = getCityByLanguage(city, language);
    if (translated) {
      // Use lowercase as key to detect duplicates
      const key = translated.toLowerCase();
      if (!uniqueCities.has(key)) {
        uniqueCities.set(key, translated);
      }
    }
  });

  return Array.from(uniqueCities.values()).sort((a, b) => a.localeCompare(b, language));
};

export default {
  formatLocation,
  getCityByLanguage,
  formatCityCountry,
  formatFullAddress,
  transformCitiesByLanguage,
};
